import { z } from 'zod';
import { currencyCodeSchema, uuidSchema } from './common';

/**
 * 📥 CSV bank statement import — kolon eşleme protokolü.
 *
 * Akış:
 *  1. POST /me/csv-import/preview (multipart)
 *     → backend dosyayı parse eder, otomatik kolon tahmini + ilk 10 satır döner
 *  2. Kullanıcı kolon eşlemesini doğrular/düzeltir
 *  3. POST /me/csv-import/commit (JSON body)
 *     → tüm satırları parse eder, duplicate detection sonra transaction olarak insert eder
 */

export const csvColumnMappingSchema = z.object({
  date: z.string().min(1),
  amount: z.string().min(1),
  description: z.string().min(1).optional(),
  counterparty: z.string().min(1).optional(),
  reference: z.string().min(1).optional(),
  /** Bazı bankalar pozitif/negatifi ayrı sütunda verir (credit/debit) */
  amountIn: z.string().min(1).optional(),
  amountOut: z.string().min(1).optional(),
});
export type CsvColumnMapping = z.infer<typeof csvColumnMappingSchema>;

export const csvImportOptionsSchema = z.object({
  /** Sayı formatı: DE/AT/FR/NL `1.234,56` veya US/UK `1,234.56` */
  decimalSeparator: z.enum(['.', ',']).default(','),
  /** Date format: 'iso' (YYYY-MM-DD) | 'dmy' (DD.MM.YYYY) | 'mdy' (MM/DD/YYYY) */
  dateFormat: z.enum(['iso', 'dmy', 'mdy']).default('dmy'),
  delimiter: z.enum([',', ';', '\t']).optional(),
  skipFirstRow: z.boolean().default(true),
  currency: currencyCodeSchema.default('EUR'),
});
export type CsvImportOptions = z.infer<typeof csvImportOptionsSchema>;

export const csvImportPreviewSchema = z.object({
  moneyAccountId: uuidSchema,
  csv: z.string().min(1).max(5_000_000), // ~5MB max
  options: csvImportOptionsSchema.partial().optional(),
});
export type CsvImportPreviewInput = z.infer<typeof csvImportPreviewSchema>;

export const csvImportCommitSchema = z.object({
  moneyAccountId: uuidSchema,
  csv: z.string().min(1).max(5_000_000),
  mapping: csvColumnMappingSchema,
  options: csvImportOptionsSchema,
  /** Budget period id'sini "Diğer / Import" kartına yazmak için */
  budgetPeriodId: uuidSchema.optional(),
});
export type CsvImportCommitInput = z.infer<typeof csvImportCommitSchema>;

/**
 * 📥 OFX bank statement import.
 *  - OFX 1.x (SGML) ve 2.x (XML) destekli; FITID otomatik duplicate detection
 *  - Tek adımlı: preview yok, çünkü OFX yapısı tahmin gerektirmez
 */
export const ofxImportSchema = z.object({
  moneyAccountId: uuidSchema,
  ofx: z.string().min(10).max(10_000_000), // ~10MB max (OFX bigger than CSV)
  currency: currencyCodeSchema.default('EUR'),
  budgetPeriodId: uuidSchema.optional(),
});
export type OfxImportInput = z.infer<typeof ofxImportSchema>;

/**
 * 📥 QIF (Quicken Interchange Format) import.
 *  - `^` ile ayrılmış işlem block'ları, single-letter prefixed alanlar
 *  - Date format: bölgeye göre değişir (US MDY, EU DMY) — kullanıcı seçer
 */
export const qifImportSchema = z.object({
  moneyAccountId: uuidSchema,
  qif: z.string().min(2).max(5_000_000),
  currency: currencyCodeSchema.default('EUR'),
  dateFormat: z.enum(['dmy', 'mdy', 'iso']).default('dmy'),
  budgetPeriodId: uuidSchema.optional(),
});
export type QifImportInput = z.infer<typeof qifImportSchema>;

/**
 * 📥 CAMT.053 (ISO 20022 Bank-to-Customer Statement) import.
 *  - EU bankalarının modern XML standardı (DE/NL/FR/BE bankaları üretir)
 *  - SEPA EndToEndId duplicate detection için natural key
 *  - Currency XML'in <Ccy> attribute'ından okunur (override edilebilir)
 */
export const camt053ImportSchema = z.object({
  moneyAccountId: uuidSchema,
  xml: z.string().min(50).max(20_000_000), // ~20MB max (CAMT genelde büyük)
  /** XML'de Ccy yoksa veya override istiyorsan. */
  currency: currencyCodeSchema.optional(),
  budgetPeriodId: uuidSchema.optional(),
});
export type Camt053ImportInput = z.infer<typeof camt053ImportSchema>;

/**
 * 📥 MT940 (SWIFT) banka ekstre formatı.
 *  - DE/AT/CH bankalarının geleneksel formatı, hâlâ yaygın
 *  - `:20:` `:25:` `:60F:` ... `:61:` ... `:86:` ... `:62F:` field tabanlı
 *  - Amount format: virgül decimal (1500,00), D/C indicator işareti belirler
 */
export const mt940ImportSchema = z.object({
  moneyAccountId: uuidSchema,
  mt940: z.string().min(10).max(5_000_000),
  /** Mevcut değilse fallback — `:25:` IBAN/CCY'sinden tespit edilebilir. */
  currency: currencyCodeSchema.optional(),
  budgetPeriodId: uuidSchema.optional(),
});
export type Mt940ImportInput = z.infer<typeof mt940ImportSchema>;
